import React, { useState, useEffect } from 'react';
import AppSpinner from "../Spinner/AppSpinner";
import { Modal, Button, Form, Toast, ToastContainer } from 'react-bootstrap';
import { BASE_URL } from '../../constants';
import { apiFetch } from "../../utils/apiFetch";
import { useApiError } from '../../utils/useApiError';
import ValidationErrorModal from '../ValidationErrorModal/ValidationErrorModal';

const GroupEditModal = ({ show, handleClose, groupId, groupData, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isGlobalLoading, setIsGlobalLoading] = useState(false);
  
  const [groupName, setGroupName] = useState('');
  const [lessonDays, setLessonDays] = useState([]);
  const [willGraduateDate, setWillGraduateDate] = useState('');

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastVariant, setToastVariant] = useState("danger");

  const { errorModal, clearError, handleApiError } = useApiError();

  const triggerToast = (msg, variant = "danger") => {
    setToastMessage(msg);
    setToastVariant(variant);
    setShowToast(true);
  };

  useEffect(() => {
    if (!show || !groupId) return;

    if (groupData) {
      setGroupName(groupData.name);
      setLessonDays(groupData.groupLessonDays || []);
      setWillGraduateDate(groupData.willGraduateDate || '');
      return;
    }

    const fetchGroupDetails = async () => {
      setLoading(true);
      try {
        const res = await apiFetch(`${BASE_URL}/teacher-groups/${groupId}`);
        const data = await res.json();
        if (data && data.teacherGroup) {
          setGroupName(data.teacherGroup.name);
          setLessonDays(data.teacherGroup.groupLessonDays || []);
          setWillGraduateDate(data.teacherGroup.willGraduateDate || '');
        }
      } catch (err) { console.error("Fetch error:", err); }
      finally { setLoading(false); }
    };
    fetchGroupDetails();
  }, [show, groupId, groupData]);

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    setSaving(true);

    const payload = {
      groupId: Number(groupId),
      groupName: groupName,
      groupLessonDays: lessonDays.map(ld => {
        const timeStr = ld.hour?.length === 5 ? `${ld.hour}:00` : (ld.hour || "00:00:00");
        return {
          day: parseInt(ld.day),
          hour: timeStr
        };
      }),
      willGraduateDate: willGraduateDate || null
    };

    try {
      const res = await apiFetch(`${BASE_URL}/teacher-groups/change-name`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        handleClose();
        setSaving(false);
        setIsGlobalLoading(true);
        triggerToast("Məlumatlar uğurla yeniləndi", "success");
        setTimeout(async () => {
          if (onSuccess) await onSuccess();
          setIsGlobalLoading(false);
        }, 1500);
      } else {
        const data = await res.json().catch(() => ({}));
        handleApiError(data);
        setSaving(false);
      }
    } catch (err) {
      handleApiError({ message: 'Server xətası baş verdi.' });
      setSaving(false);
    }
  };

  const handleDayTimeChange = (index, field, value) => {
    const updated = [...lessonDays];
    updated[index][field] = value;
    setLessonDays(updated);
  };

  return (
    <>
      {isGlobalLoading && (
        <div style={{
          position: 'fixed', top: 0, left: 0,
          width: '100vw', height: '100vh',
          backgroundColor: 'rgba(255, 255, 255, 0.9)', 
          display: 'flex', flexDirection: 'column',
          justifyContent: 'center', alignItems: 'center', zIndex: 9999
        }}>
          <div className="text-center">
            <AppSpinner inline />
            <p className="text-muted small mt-2">Məlumatlar yenilənir...</p>
          </div>
        </div>
      )}

      <Modal show={show} onHide={handleClose} centered backdrop="static" dialogClassName="modal-90w" contentClassName="border-0 shadow-lg rounded-4">
        <style>{`.modal-90w { max-width: 400px; }`}</style>
        <Modal.Header closeButton className="py-2 px-3 bg-light rounded-top-4 border-0">
          <Modal.Title className="fs-6 fw-bold text-dark">
            <i className="bi bi-pencil-square text-success me-2"></i> Redaktə
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-3">
          {loading ? (
            <div className="text-center py-4"><AppSpinner inline /></div>
          ) : (
            <Form onSubmit={handleSave}>
              <Form.Group className="mb-3">
                <Form.Label className="small fw-bold text-secondary mb-1">Qrup Adı</Form.Label>
                <Form.Control type="text" size="sm" value={groupName} onChange={(e) => setGroupName(e.target.value)} required />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="small fw-bold text-secondary mb-1">
                  <i className="bi bi-calendar-check me-1 text-success"></i>Məzun olma tarixi
                </Form.Label>
                <Form.Control 
                  type="date" 
                  size="sm" 
                  value={willGraduateDate} 
                  onChange={(e) => setWillGraduateDate(e.target.value)} 
                />
              </Form.Group>

              <div className="p-2 border rounded bg-light" style={{ maxHeight: '180px', overflowY: 'auto' }}>
                {lessonDays.map((ld, index) => (
                  <div key={index} className="d-flex align-items-center gap-2 mb-2 bg-white p-1 rounded border shadow-sm">
                    <Form.Select size="sm" value={ld.day} onChange={(e) => handleDayTimeChange(index, 'day', e.target.value)} className="border-0 bg-transparent" style={{ fontSize: '12px' }}>
                      <option value={1}>B.E.</option><option value={2}>Ç.A.</option><option value={3}>Çərş.</option>
                      <option value={4}>C.A.</option><option value={5}>Cümə</option><option value={6}>Şən.</option><option value={7}>Baz.</option>
                    </Form.Select>
                    <Form.Control type="time" size="sm" value={ld.hour?.substring(0, 5)} onChange={(e) => handleDayTimeChange(index, 'hour', e.target.value)} className="border-0 bg-transparent fw-bold" style={{ fontSize: '12px' }} required />
                    <Button variant="link" className="text-danger p-0 ms-auto" onClick={() => setLessonDays(lessonDays.filter((_, i) => i !== index))}><i className="bi bi-dash-circle-fill"></i></Button>
                  </div>
                ))}
                <Button variant="outline-primary" size="sm" className="w-100 py-1 mt-1 shadow-sm" onClick={() => setLessonDays([...lessonDays, { day: 1, hour: "09:00:00" }])} style={{ fontSize: '11px', borderStyle: 'dashed' }}>+ Yeni gün əlavə et</Button>
              </div>
            </Form>
          )}
        </Modal.Body>

        <Modal.Footer className="py-2 px-3 border-0 bg-light-subtle rounded-bottom-4">
          <Button variant="outline-secondary" size="sm" onClick={handleClose}>Ləğv et</Button>
          <Button variant="success" size="sm" onClick={handleSave} disabled={saving || loading} style={{ backgroundColor: '#10b981', border: 'none' }}>
            {saving ? <AppSpinner inline /> : 'Yadda saxla'}
          </Button>
        </Modal.Footer>
      </Modal>

      <ToastContainer position="bottom-end" className="p-3" style={{ zIndex: 10000 }}>
        <Toast onClose={() => setShowToast(false)} show={showToast} delay={4000} autohide bg={toastVariant}>
          <Toast.Header closeButton={false}>
            <strong className="me-auto">Sistem Mesajı</strong>
            <small>İndi</small>
          </Toast.Header>
          <Toast.Body className="text-white">{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>
      <ValidationErrorModal show={!!errorModal} onHide={clearError} message={errorModal?.message} validations={errorModal?.validations} />
    </>
  );
};

export default GroupEditModal;